// Helper to build a LocalBusiness JSON-LD node for neighborhood service pages.
// Used by the city-level smart-home / home-theater installation routes so each
// page emits a fully qualified LocalBusiness object that's eligible for
// Google's local rich results.

type LocalBusinessArgs = {
  neighborhood: string;
  /** Service-area page URL — used as the @id and url. */
  url: string;
  /** Hero image for the page (absolute URL preferred; relative is normalized). */
  image: string;
  /** ZIP code for the neighborhood. */
  zip: string;
  /** Approximate latitude of the neighborhood centroid. */
  latitude: number;
  /** Approximate longitude of the neighborhood centroid. */
  longitude: number;
  /** Lowest typical project price in USD (e.g. 15000 for smart home, 25000 for theater). */
  priceFloor: number;
  /** Name of the service the page is about, e.g. "Smart Home Installation". */
  serviceName: string;
};

export const SITE = "https://apexav.com";

/** Canonical @id for the single Apex AV business entity used site-wide. */
export const BUSINESS_ID = `${SITE}/#organization`;

/** Lightweight reference to the canonical business entity. */
export const BUSINESS_REF = { "@id": BUSINESS_ID } as const;

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "2921 W 38th AVE STE 169",
  addressLocality: "Denver",
  addressRegion: "CO",
  postalCode: "80211",
  addressCountry: "US",
} as const;

const OPENING_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "18:00",
  },
  { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
] as const;

const SAME_AS = [
  "https://share.google/jMyCOceBfsaHfeVQp",
  "https://x.com/ApexAV80211",
  "https://www.facebook.com/apexavdenver",
] as const;

/** Cities and towns Apex AV serves — used for areaServed on the primary entity. */
export const AREAS_SERVED = [
  "Denver, CO", "Aurora, CO", "Lakewood, CO", "Centennial, CO",
  "Highlands Ranch, CO", "Littleton, CO", "Englewood, CO", "Cherry Creek, CO",
  "Parker, CO", "Castle Rock, CO", "Castle Pines, CO", "Franktown, CO",
  "Thornton, CO", "Westminster, CO", "Arvada, CO", "Broomfield, CO",
  "Boulder, CO", "Golden, CO",
] as const;

/**
 * The single canonical LocalBusiness node for Apex AV.
 * Emitted site-wide from __root.tsx so every page carries local business
 * markup; every other node references it by @id instead of redefining it.
 */
export const PRIMARY_LOCAL_BUSINESS = {
  "@type": ["Organization", "LocalBusiness", "HomeAndConstructionBusiness"],
  "@id": BUSINESS_ID,
  name: "Apex AV",
  legalName: "Apex AV LLC",
  alternateName: ["Apex AV Denver", "Apex Audio Video", "Apex AV Colorado"],
  url: `${SITE}/`,
  slogan: "Denver's home theater and smart home integration specialists",
  foundingDate: "2015",
  foundingLocation: { "@type": "Place", name: "Denver, Colorado" },
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 2, maxValue: 10 },
  description:
    "Denver home automation and AV installation company specializing in custom home theaters, smart home integration, whole-home audio, lighting control, motorized shading, security, and networking.",
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/logo.png`,
    width: 512,
    height: 512,
    caption: "Apex AV",
  },
  telephone: "+1-303-317-3233",
  email: "info@apexav.com",
  priceRange: "$$$",
  address: POSTAL_ADDRESS,
  geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
  areaServed: AREAS_SERVED.map((name) => ({ "@type": "City", name })),
  serviceArea: {
    "@type": "GeoCircle",
    geoMidpoint: { "@type": "GeoCoordinates", latitude: 39.7392, longitude: -104.9903 },
    geoRadius: 80000,
  },
  openingHoursSpecification: OPENING_HOURS,
  sameAs: SAME_AS,
  hasMap: "https://share.google/jMyCOceBfsaHfeVQp",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Check, Credit Card, ACH",
  knowsAbout: [
    "Home theater design and installation",
    "Smart home automation",
    "Control4 home automation",
    "Whole-home audio",
    "Lighting control and motorized shades",
    "Structured wiring and home networking",
    "Security cameras and access control",
    "New construction AV prewire",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Theater Installation" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Smart Home Automation" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Whole-Home Audio" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Networking" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Lighting & Shading Control" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Security Cameras" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "AV Prewire for New Construction" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "TV Mounting" } },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-303-317-3233",
      email: "info@apexav.com",
      contactType: "customer service",
      areaServed: "US-CO",
      availableLanguage: "English",
    },
    {
      "@type": "ContactPoint",
      telephone: "+1-303-317-3233",
      email: "info@apexav.com",
      contactType: "sales",
      areaServed: "US-CO",
      availableLanguage: "English",
    },
  ],
  potentialAction: {
    "@type": "ReserveAction",
    name: "Request a consultation",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE}/contact`,
      inLanguage: "en-US",
      actionPlatform: [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform",
      ],
    },
  },
} as const;


/** Build a Service node that credits the canonical business as provider. */
export function serviceSchema(args: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
  /** City / place name, or omit for the whole metro. */
  areaServed?: string;
  price?: string | number;
}) {
  return {
    "@type": "Service",
    name: args.name,
    description: args.description,
    url: args.url,
    serviceType: args.serviceType,
    provider: BUSINESS_REF,
    areaServed: args.areaServed
      ? { "@type": "City", name: args.areaServed }
      : { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
    ...(args.price
      ? { offers: { "@type": "Offer", priceCurrency: "USD", price: String(args.price) } }
      : {}),
  };
}

/** Build a BreadcrumbList node from [name, url] pairs. */
export function breadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function localBusinessSchema(args: LocalBusinessArgs) {
  const imageUrl = args.image.startsWith("http") ? args.image : `${SITE}${args.image}`;
  return {
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": `${args.url}#localbusiness`,
    name: `Apex AV — ${args.serviceName} in ${args.neighborhood}`,
    image: imageUrl,
    logo: `${SITE}/logo.png`,
    url: args.url,
    telephone: "+1-303-317-3233",
    email: "info@apexav.com",
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2921 W 38th AVE STE 169",
      addressLocality: "Denver",
      addressRegion: "CO",
      postalCode: "80211",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 39.7714,
      longitude: -105.0234,
    },
    areaServed: [
      { "@type": "Place", name: `${args.neighborhood}, Denver, CO` },
      { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "16:00",
      },
    ],
    sameAs: [
      "https://share.google/jMyCOceBfsaHfeVQp",
      "https://x.com/ApexAV80211",
      "https://www.facebook.com/apexavdenver",
    ],
    parentOrganization: { "@id": `${SITE}/#organization` },
    makesOffer: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: args.priceFloor,
      itemOffered: {
        "@type": "Service",
        name: `${args.serviceName} in ${args.neighborhood}`,
      },
    },
  };
}

/** Coordinate + ZIP lookup for the neighborhoods we currently have pages for. */
export const NEIGHBORHOOD_GEO: Record<
  string,
  { name: string; zip: string; latitude: number; longitude: number }
> = {
  "cherry-creek": { name: "Cherry Creek", zip: "80206", latitude: 39.7176, longitude: -104.9525 },
  "highlands-ranch": { name: "Highlands Ranch", zip: "80129", latitude: 39.5410, longitude: -104.9700 },
  "castle-rock": { name: "Castle Rock", zip: "80104", latitude: 39.3722, longitude: -104.8561 },
  "castle-pines": { name: "Castle Pines", zip: "80108", latitude: 39.4583, longitude: -104.8742 },
};
